import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TSvgProps } from '@r1-frontend/ui-react/components/svg/createSvg';
import SteppableProgressBar from '@r1-frontend/ui-react/organisms/SteppableProgressBar';

import { MEGABIT_AVAIL_CODES_LIST, MEGABIT_SPEED } from '~/src/features/full-buy/consts';
import isOneLineAddressScreen from '~/src/features/full-buy/helpers/ab/isOneLineAddressScreen';
import { goToPrevStep } from '~/src/features/full-buy/store/actions/goToPrevStep';
import { isHaveSelectedProducts } from '~/src/features/full-buy/store/selectors/products/isHaveSelectedProducts';
import { selectCurrentTariff } from '~/src/features/full-buy/store/selectors/selectCurrentTariff';
import { ESteps } from '~/src/features/full-buy/types';
import { TState } from '~/src/store';

type TGetProgressTitlesArgument = {
    step: ESteps,
    hasProducts: boolean,
    canConnectAfterCheckedAddress: boolean,
    isEdit: boolean,
    hasError: boolean,
    isAddressVerificationOnFirstStep: boolean,
}

const getProgressTitles = ({
    step,
    hasProducts,
    canConnectAfterCheckedAddress,
    isEdit,
    hasError,
    isAddressVerificationOnFirstStep,
}: TGetProgressTitlesArgument): { title: string, imgName?: string, hasPrevBtn?: boolean } => {
    switch (step) {
        case ESteps.tariffInfo: {
            return {
                title: 'Что входит в стоимость тарифа',
            };
        }
        case ESteps.subscriptions: {
            return {
                title: 'Выбор подписок',
                hasPrevBtn: true,
            };
        }
        case ESteps.equipments: {
            return {
                title: hasProducts ? 'Уже пакуем роутер...' : 'Выбор роутера',
                imgName: hasProducts ? 'box-packing.gif' : '',
                hasPrevBtn: true,
            };
        }
        case ESteps.equipmentsTv: {
            return {
                title: 'Выбор приставки',
                hasPrevBtn: true,
            };
        }
        case ESteps.equipmentsCottage: {
            return {
                title: 'Выбор оборудования',
                hasPrevBtn: true,
            };
        }
        case ESteps.address: {
            return {
                title: (canConnectAfterCheckedAddress && !isEdit) ? 'Уже прокладываем маршрут...' : 'Проверка адреса',
                imgName: (canConnectAfterCheckedAddress && !isEdit) ? 'dog-walking.gif' : '',
                hasPrevBtn: !isAddressVerificationOnFirstStep,
            };
        }
        case ESteps.contacts: {
            return {
                title: 'Ввод данных',
                hasPrevBtn: true,
            };
        }
        case ESteps.agreementCreated: {
            return {
                title: 'Осталось совсем чуть-чуть...',
            };
        }
        case ESteps.timeSlots: {
            return {
                title: 'Остался последний шаг',
            };
        }
        case ESteps.final: {
            return {
                title: hasError ? 'Получили вашу заявку, но есть нюанс' : 'Вы молодец! Заявка отправлена.',
                imgName: 'lightblue-paper-airplane.gif',
            };
        }
        default: {
            const unknownAction: never = step;
            return {
                title: '',
            } || unknownAction;
        }
    }
};

interface IProgressBarWithBackBtnContainer {
    Icon?: (props: TSvgProps) => JSX.Element,
    onClickInfoIcon?: () => void,
}

const ProgressBarWithBackBtnContainer = ({
    Icon,
    onClickInfoIcon,
}: IProgressBarWithBackBtnContainer): JSX.Element | null => {
    const {
        fullBuy: { stepIndex, flow, agreement: agreementData },
        contactAddress: { isEdit, verifiedAddressData: { gigabitAvail, isHouseConnected } },
        hasProducts,
        selectedTariff,
        selectedAddress,
        isAddressVerificationOnFirstStep,
    } = useSelector((state: TState) => ({
        fullBuy: state.fullBuy,
        hasProducts: isHaveSelectedProducts(state),
        contactAddress: state.contactAddress,
        selectedTariff: selectCurrentTariff(state),
        selectedAddress: state.fullBuy.selectedAddress,
        isAddressVerificationOnFirstStep: state.fullBuy._addressVerificationOnFirstStep,
    }));

    const isOneLineAddressFormScreen = isOneLineAddressScreen();

    const dispatch = useDispatch();

    const { speed: tariffSpeed } = selectedTariff || {};

    const canConnectAfterCheckedAddress = useMemo(() => {
        const _gigabitMayConnect = MEGABIT_AVAIL_CODES_LIST.includes(gigabitAvail);

        const _requiredGigaSpeed = !!tariffSpeed && (tariffSpeed > MEGABIT_SPEED);

        const _shouldConnect = _requiredGigaSpeed
            ? (isHouseConnected && _gigabitMayConnect)
            : isHouseConnected;

        return isOneLineAddressFormScreen
            ? Boolean(selectedAddress?.isConnectionAvailable)
            : (_shouldConnect && !isEdit);
    }, [isHouseConnected, gigabitAvail, tariffSpeed, selectedAddress]);

    const onClickHandler = () => {
        dispatch(goToPrevStep());
    };

    if (!flow) {
        return null;
    }

    const progress = (stepIndex + 1) / flow.steps.length;
    const currentStep = flow.steps[stepIndex];

    const dynamicProps = getProgressTitles({
        step: currentStep,
        hasProducts,
        canConnectAfterCheckedAddress,
        isEdit: isOneLineAddressFormScreen ? !selectedAddress : isEdit,
        hasError: agreementData.hasError,
        isAddressVerificationOnFirstStep,
    });

    const props = {
        progress: progress,
        imgPath: '/static/images/full-buy/for-progress-bar',
        onClick: dynamicProps.hasPrevBtn ? onClickHandler : undefined,
        Icon: Icon,
        onClickInfoIcon: onClickInfoIcon,

        ...dynamicProps,
    };

    return <SteppableProgressBar {...props} />;
};
export default ProgressBarWithBackBtnContainer;
